import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Paperclip, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { accountsFileUrl, addBill, addVendor, billPaid, listBills, listVendors } from "@/lib/api";
import { FilePick } from "@/components/accounts/RequestQueue";
import { Empty, Stat, TH, TR, errMsg, money } from "@/components/accounts/shared";

const CATS = ["Rent", "Utilities", "Salaries", "Ad Spend", "Marketing", "Management Fee", "Lead Nurture Fee", "Equipment", "Software", "Maintenance", "Supplies", "Other"];
const SELECT = "w-full bg-[#0a0a0a] border border-[#262626] rounded-md h-9 px-2 text-sm";
const today = () => new Date().toISOString().slice(0, 10);

// Vendor bills: rent, utilities, suppliers. Unpaid / overdue view; paid bills are bank debits in reconciliation.
export default function Payables({ country, gym, gyms }) {
  const [bills, setBills] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [show, setShow] = useState("unpaid");
  const [adding, setAdding] = useState(false);
  const [paying, setPaying] = useState(null);
  const load = useCallback(async () => {
    try { const [b, v] = await Promise.all([listBills(), listVendors()]); setBills(b); setVendors(v); }
    catch (e) { toast.error(errMsg(e, "Could not load bills")); }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!bills) return <div className="py-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-zinc-500" /></div>;
  const ctry = Object.fromEntries(gyms.map((g) => [g.id, g.country]));
  const mine = bills.filter((b) => (!gym || b.facility_id === gym) && (!country || ctry[b.facility_id] === country));
  const rows = mine.filter((b) => (show === "all" ? true : show === "overdue" ? b.overdue : b.status === show));
  const byCur = {};
  mine.filter((b) => b.status === "unpaid").forEach((b) => {
    const k = b.currency_code;
    byCur[k] = byCur[k] || { ...b, due: 0, overdue_amt: 0 };
    byCur[k].due += b.amount;
    if (b.overdue) byCur[k].overdue_amt += b.amount;
  });
  return (
    <div className="space-y-4" data-testid="accounts-payables">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
        {Object.values(byCur).map((c) => <Stat key={c.currency_code} label={`Unpaid · ${c.currency_code}`} value={money(c, c.due)} sub={`${money(c, c.overdue_amt)} overdue`} tone={c.overdue_amt ? "bad" : undefined} />)}
        {!Object.keys(byCur).length && <Stat label="Unpaid" value="Nothing due" tone="good" />}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex gap-1.5">{["unpaid", "overdue", "paid", "all"].map((s) => (
          <button key={s} onClick={() => setShow(s)} className={`text-xs px-3 py-1 rounded-full border ${show === s ? "border-blue-500/60 bg-blue-500/10 text-blue-200" : "border-[#262626] text-zinc-400"}`}>{s[0].toUpperCase() + s.slice(1)}</button>
        ))}</div>
        <Button size="sm" onClick={() => setAdding(true)} className="bg-blue-600 hover:bg-blue-500 h-8" data-testid="bill-add"><Plus className="h-3.5 w-3.5 mr-1" /> Bill</Button>
      </div>
      <div className="card-surface overflow-x-auto">
        {rows.length === 0 ? <Empty>No bills here.</Empty> : (
          <table className="w-full text-sm min-w-[820px]">
            <thead><tr className={TH}><th className="px-3 py-2 font-medium">Due</th><th className="px-3 py-2 font-medium">Vendor</th><th className="px-3 py-2 font-medium">Gym</th>
              <th className="px-3 py-2 font-medium">Category</th><th className="px-3 py-2 font-medium text-right">Amount</th><th className="px-3 py-2 font-medium">Status</th><th /></tr></thead>
            <tbody>
              {rows.map((b) => (
                <tr key={b.id} className={TR}>
                  <td className={`px-3 py-2 tabular-nums ${b.overdue ? "text-rose-300" : "text-zinc-400"}`}>{b.due_date}{b.overdue && <div className="text-[10px]">{b.days_overdue} d overdue</div>}</td>
                  <td className="px-3 py-2 text-zinc-200">{b.vendor}<div className="text-[10px] text-zinc-500">{b.bill_no} · {b.bill_date}</div>
                    {b.attachment_id && <a href={accountsFileUrl(b.attachment_id)} target="_blank" rel="noreferrer" className="text-[10px] text-blue-300 inline-flex items-center gap-0.5"><Paperclip className="h-3 w-3" /> bill</a>}</td>
                  <td className="px-3 py-2 text-zinc-400">{b.gym}</td>
                  <td className="px-3 py-2 text-xs text-zinc-400">{b.category}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(b, b.paid_amount || b.amount)}{b.input_tax > 0 && <div className="text-[10px] text-zinc-500">incl. {money(b, b.input_tax, true)} tax</div>}</td>
                  <td className="px-3 py-2 text-xs text-zinc-400">{b.status}{b.paid_date && <div className="text-[10px] text-zinc-500">{b.paid_date} · {b.bank_ref}{b.expense_link === "ad_split" ? " · Ad Spend comes from Meta" : b.expense_link ? ` · expense ${b.expense_link}` : ""}</div>}</td>
                  <td className="px-3 py-2 text-right">{b.status === "unpaid" && <Button size="sm" onClick={() => setPaying(b)} className="h-7 bg-blue-600 hover:bg-blue-500">Mark paid</Button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {adding && <BillDialog vendors={vendors} gyms={gyms} onClose={() => setAdding(false)} onDone={() => { setAdding(false); load(); }} />}
      {paying && <BillPaid b={paying} onClose={() => setPaying(null)} onDone={() => { setPaying(null); load(); }} />}
    </div>
  );
}

function BillDialog({ vendors, gyms, onClose, onDone }) {
  const [f, setF] = useState({ vendor_id: "", new_vendor: "", facility_id: "", bill_no: "", bill_date: today(), due_date: today(), amount: "", input_tax: "", category: "Rent", attachment_id: null });
  const save = async () => {
    try {
      let vid = f.vendor_id;
      if (!vid && f.new_vendor.trim()) vid = (await addVendor({ name: f.new_vendor, category: f.category })).id;
      await addBill({ ...f, vendor_id: vid, amount: Number(f.amount), input_tax: Number(f.input_tax) || 0 });
      toast.success("Bill added");
      onDone();
    } catch (e) { toast.error(errMsg(e, "Could not add the bill")); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100 max-w-md">
        <DialogHeader><DialogTitle>Add a bill</DialogTitle></DialogHeader>
        <div className="space-y-2 text-sm">
          <select value={f.vendor_id} onChange={(e) => setF({ ...f, vendor_id: e.target.value })} className={SELECT}>
            <option value="">New vendor…</option>{vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
          {!f.vendor_id && <Input value={f.new_vendor} onChange={(e) => setF({ ...f, new_vendor: e.target.value })} placeholder="Vendor name" className="bg-[#0a0a0a] border-[#262626]" />}
          <select value={f.facility_id} onChange={(e) => setF({ ...f, facility_id: e.target.value })} className={SELECT}>
            <option value="">Gym…</option>{gyms.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
          <div className="grid grid-cols-2 gap-2">
            <Input value={f.bill_no} onChange={(e) => setF({ ...f, bill_no: e.target.value })} placeholder="Bill number" className="bg-[#0a0a0a] border-[#262626]" />
            <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className={SELECT}>{CATS.map((c) => <option key={c}>{c}</option>)}</select>
            <label className="text-[11px] text-zinc-500">Bill date<Input type="date" value={f.bill_date} onChange={(e) => setF({ ...f, bill_date: e.target.value })} className="bg-[#0a0a0a] border-[#262626]" /></label>
            <label className="text-[11px] text-zinc-500">Due<Input type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} className="bg-[#0a0a0a] border-[#262626]" /></label>
            <Input type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} placeholder="Amount (incl. tax)" className="bg-[#0a0a0a] border-[#262626]" />
            <Input type="number" value={f.input_tax} onChange={(e) => setF({ ...f, input_tax: e.target.value })} placeholder="Tax on the bill" className="bg-[#0a0a0a] border-[#262626]" />
          </div>
          <FilePick onUploaded={(id) => setF({ ...f, attachment_id: id })} label="Bill (optional)" />
        </div>
        <DialogFooter><Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={!(f.vendor_id || f.new_vendor.trim()) || !f.facility_id || !Number(f.amount)} className="bg-blue-600 hover:bg-blue-500">Add</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BillPaid({ b, onClose, onDone }) {
  const [f, setF] = useState({ paid_date: today(), bank_ref: "", post_expense: true });
  const save = async () => {
    try {
      const r = await billPaid(b.id, f);
      toast.success(r.expense_link === "ad_split" ? "Paid · this month's Ad Spend already comes from Meta, so it isn't added again" : r.expense_link === "linked" ? "Paid · linked to the expense the gym already entered" : r.expense_link ? "Paid · added to Expenses" : "Paid");
      onDone();
    } catch (e) { toast.error(errMsg(e, "Could not save")); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100 max-w-sm">
        <DialogHeader><DialogTitle>Pay {b.vendor}</DialogTitle></DialogHeader>
        <Input type="date" value={f.paid_date} onChange={(e) => setF({ ...f, paid_date: e.target.value })} className="bg-[#0a0a0a] border-[#262626]" />
        <Input value={f.bank_ref} onChange={(e) => setF({ ...f, bank_ref: e.target.value })} placeholder="Bank reference" className="bg-[#0a0a0a] border-[#262626]" />
        <label className="flex items-center gap-2 text-xs text-zinc-400"><input type="checkbox" checked={f.post_expense} onChange={(e) => setF({ ...f, post_expense: e.target.checked })} className="accent-blue-500" />
          Put it in Expenses (links to the gym's own entry if it's already there)</label>
        <DialogFooter><Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={!f.bank_ref.trim()} className="bg-blue-600 hover:bg-blue-500">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
